import { useEffect, useRef, useState } from 'react';
import { Users, MoreHorizontal } from 'lucide-react';
import OsaFazi, { BARVA_FAZE } from './OsaFazi';
import { avatarGradient, initials, phaseSectionStyle } from './ShiftCardHeader';

/**
 * Stálá část Mojí směny — horní panel nad pracovní plochou (návrh „Horní
 * panel směny", 2026-10-12).
 *
 * Kostra je ve všech fázích stejná a **stejně vysoká**: stav a čas, osa
 * fází, tři dlaždice, akce. Fáze mění jen obsah a barvu tečky, ne rozložení,
 * takže práce pod panelem při zahájení ani ukončení neposkočí.
 *
 *  - **Horní řádek** — vlevo tečka a stav (před zahájením název směny,
 *    potom fáze), vpravo čas: „začíná za", „zbývá", „příprava".
 *  - **Osa fází** — tři segmenty; kolik zbývá, nese horní řádek.
 *  - **Dlaždice** Auto, Pokladna, Kolegové — neutrální podklad, barevná jen
 *    ikona. Živá (auto převzaté, pokladna otevřená) má zelenou tečku, co
 *    chybí, je čárkované oranžově.
 *  - **Akce** (`ShiftCardCta`) — plné tlačítko 56 px, během průběhu tiché.
 *
 * @param {string} barvaFaze - barva fáze (tečka před zahájením, akce)
 * @param {Array} faze - `smena.faze`; bez nich se osa nekreslí
 * @param {string|null} aktivniFazeId
 * @param {(faze) => void} [onOtevritFazi]
 * @param {string} [stred] - čas vpravo nahoře během směny (zbývá / příprava)
 * @param {string} [nadpis] - název směny; jen před zahájením, místo fáze
 * @param {string} [nadpisDoplnek] - čas vpravo nahoře před zahájením
 * @param {string} [nadpisDoplnekBarva] - jeho barva (blíží se oranžová, zpoždění červená)
 * @param {string} [podnadpis] - doplněk k názvu (sloučené směny), na témže řádku
 * @param {Array} sloty - dlaždice auto/pokladna (props jako `CockpitSlot`)
 * @param {Array} tym - kolegové na směně
 * @param {() => void} [onTym]
 * @param {ReactNode} [children] - obsah uvnitř panelu pod dlaždicemi
 * @param {boolean} [sbaleno] - zasunout `children` (animovaně)
 * @param {ReactNode} [akce] - `ShiftCardCta`
 * @param {boolean} [kompaktni] - průběh směny na mobilu (Cockpit 2.0, 2026-10-19):
 *   jeden řádek, tenká čára času místo osy fází, dlaždice jako čipy, akce
 *   v nabídce ⋯. Panel, který je vidět celou směnu, má ~150 px místo ~275.
 * @param {Array} [nabidka] - položky nabídky ⋯ v kompaktním režimu: { label, onClick, disabled, popis }
 * @param {number|null} [pomerCasu] - kolik směny uběhlo (0–1) pro čáru času
 */
const NAZEV_FAZE = { zahajeni: 'Zahájení', prubeh: 'Na směně', ukonceni: 'Ukončení' };

export default function StalyPruh({
    barvaFaze,
    faze = [],
    aktivniFazeId = null,
    onOtevritFazi,
    stred = null,
    nadpis = null,
    podnadpis = null,
    sloty = [],
    tym = [],
    onTym,
    children = null,
    sbaleno = false,
    akce = null,
    tymVybrano = false,         // dlaždice Kolegové právě ukazuje svou plochu
    nadpisDoplnek = null,
    nadpisDoplnekBarva = null,
    isDark,
    isMobile = false,
    kompaktni = false,
    nabidka = [],
    pomerCasu = null,
}) {
    const stav = nadpis || NAZEV_FAZE[aktivniFazeId] || 'Směna';
    const barvaStavu = nadpis ? barvaFaze : (BARVA_FAZE[aktivniFazeId] || barvaFaze);
    const cas = nadpis ? nadpisDoplnek : stred;
    const tymKlik = tym.length > 0 && (!children || sbaleno);

    if (kompaktni) {
        const fazeTed = faze.find(x => x.id === aktivniFazeId) ?? faze[0];
        return (
            <div style={{
                ...phaseSectionStyle(barvaFaze, isDark, isMobile),
                display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 24, minWidth: 0 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 99, flexShrink: 0, background: barvaStavu, boxShadow: `0 0 0 4px ${barvaStavu}2E` }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {stav}{podnadpis && <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}> · {podnadpis}</span>}
                    </span>
                    {cas && <span style={{ flexShrink: 0, fontSize: 13.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{cas}</span>}
                </div>

                {/* Čára času místo osy fází — ťuk otevře fáze jako dřív. */}
                <button type="button" onClick={onOtevritFazi && fazeTed ? () => onOtevritFazi(fazeTed) : undefined}
                    aria-label="Fáze směny" style={{ height: 14, padding: '5px 0', border: 'none', background: 'none', cursor: onOtevritFazi ? 'pointer' : 'default', display: 'block', width: '100%' }}>
                    <span style={{ display: 'block', height: 4, borderRadius: 4, background: 'var(--surface-elevated)', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${Math.round(Math.min(1, Math.max(0, pomerCasu ?? 0)) * 100)}%`, borderRadius: 4, background: 'linear-gradient(90deg, #FF8A00, #10B981)' }} />
                    </span>
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {sloty.filter(s => !s.disabled).map(s => <Cip key={s.id} {...s} />)}
                    <Cip icon={<Users size={15} />} accent="#0CC0DF" onClick={tymKlik ? onTym : null} vybrano={tymVybrano}
                        value={tym.length === 0 ? 'Sám' : String(tym.length)} label="Kolegové" />
                    {nabidka.length > 0 && <Nabidka polozky={nabidka} />}
                </div>
                {akce}
            </div>
        );
    }

    return (
        <div style={{
            ...phaseSectionStyle(barvaFaze, isDark, isMobile),
            display: 'flex', flexDirection: 'column', gap: 12,
            flexShrink: 0,
        }}>
            {/* Stav a čas — vždycky jeden řádek, ať je výška panelu stálá. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 24, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: 99, flexShrink: 0, background: barvaStavu }} />
                <span style={{
                    flex: 1, minWidth: 0,
                    fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: barvaStavu, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }} title={podnadpis ? `${stav} · ${podnadpis}` : stav}>
                    {stav}
                    {podnadpis && <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}> · {podnadpis}</span>}
                </span>
                {cas && (
                    <span style={{
                        flexShrink: 0, fontSize: 14, fontWeight: 800,
                        color: (nadpis && nadpisDoplnekBarva) || 'var(--text-primary)',
                        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                    }}>{cas}</span>
                )}
            </div>

            {faze.length > 0 && (
                <OsaFazi faze={faze} aktivniFazeId={aktivniFazeId} onOtevrit={onOtevritFazi} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sloty.length + 1}, minmax(0, 1fr))`, gap: 8 }}>
                {sloty.map(slot => <Dlazdice key={slot.id} {...slot} />)}
                <Dlazdice
                    variant={tymKlik ? 'active' : 'readonly'}
                    icon={<Users size={15} />}
                    label="Kolegové"
                    accent="#0CC0DF"
                    // S otevřeným detailem je tým vypsaný pod dlaždicí i s akcemi;
                    // klik se zapíná až ve chvíli, kdy se detail zasune.
                    onClick={tymKlik ? onTym : null}
                    vybrano={tymVybrano}
                    value={tym.length === 0 ? 'Sám' : null}
                    valueNode={tym.length > 0 ? <AvataryTymu tym={tym} /> : null}
                />
            </div>

            {/* Rozšířený obsah uvnitř panelu — zasouvá se přes
                grid-template-rows (0fr ⇄ 1fr), jediný způsob, jak plynule
                animovat výšku, kterou dopředu neznáme. */}
            {children && (
                <div style={{
                    display: 'grid',
                    gridTemplateRows: sbaleno ? '0fr' : '1fr',
                    opacity: sbaleno ? 0 : 1,
                    marginTop: sbaleno ? -12 : 0,
                    transition: 'grid-template-rows 0.32s cubic-bezier(0.4, 0, 0.2, 1),'
                        + ' opacity 0.18s ease-out, margin-top 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: sbaleno ? 'none' : undefined,
                }}>
                    <div style={{ minHeight: 0, overflow: 'hidden' }}>
                        {children}
                    </div>
                </div>
            )}

            {akce}
        </div>
    );
}

/**
 * Dlaždice panelu — bere tytéž props jako `CockpitSlot` (sloty skládají
 * fáze směny), jen ji kreslí podle nového panelu: neutrální podklad,
 * barevná ikona, 64 px.
 *
 *   active / info — normálně; `live` = zelená tečka (věc teď běží)
 *   action        — chybí a je potřeba vybrat: čárkovaně oranžová
 *   readonly      — jen ke čtení
 *   disabled      — „Není potřeba", vyhaslá
 */
function Dlazdice({ variant = 'info', disabled = false, icon, label, value, valueNode = null, onClick = null, accent = '#3B82F6', live = false, vybrano = false }) {
    const akce = variant === 'action' && !disabled;
    const vyhasla = disabled || variant === 'readonly';
    const Wrap = onClick ? 'button' : 'div';
    const barvaIkony = akce ? '#FF8A00' : vyhasla ? 'var(--text-muted)' : accent;

    return (
        <Wrap
            type={onClick ? 'button' : undefined}
            onClick={onClick || undefined}
            aria-pressed={onClick ? vybrano : undefined}
            style={{
                position: 'relative', minWidth: 0, minHeight: 64, textAlign: 'left',
                padding: 10, borderRadius: 14, font: 'inherit', color: 'var(--text-primary)',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3,
                cursor: onClick ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent',
                background: akce ? 'rgba(255,138,0,0.07)' : disabled ? 'transparent' : 'var(--surface-elevated)',
                border: akce ? '1.5px dashed rgba(255,138,0,0.65)'
                    : vybrano ? `1.5px solid ${accent}`
                        : '1px solid var(--border-default)',
            }}>
            {live && !disabled && (
                <span aria-label="běží" style={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: 99, background: '#10B981' }} />
            )}
            {vybrano && (
                <span aria-hidden="true" style={{
                    position: 'absolute', left: '50%', bottom: -8, transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderTop: `7px solid ${accent}`,
                }} />
            )}
            <span style={{
                display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
                fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
                <span style={{ color: barvaIkony, display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
                {label}
            </span>
            {valueNode ?? (
                <span style={{
                    fontSize: 16, fontWeight: 800, lineHeight: 1.2,
                    color: akce ? '#FF8A00' : vyhasla ? 'var(--text-muted)' : 'var(--text-primary)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    fontVariantNumeric: 'tabular-nums',
                }}>{value || (disabled ? 'Ne' : '—')}</span>
            )}
        </Wrap>
    );
}

/**
 * Avatary místo čísla — na první pohled je poznat *kdo*, ne jen kolik.
 * Tři se překrývají, zbytek je „+N".
 */
function AvataryTymu({ tym }) {
    const vidi = tym.slice(0, 3);
    const zbytek = tym.length - vidi.length;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 22 }}>
            {vidi.map((m, i) => (
                <span key={m.zamestnanec_id ?? i} style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    marginLeft: i === 0 ? 0 : -7,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800, color: '#fff',
                    background: avatarGradient(m.zamestnanec_id),
                    border: '2px solid var(--surface-elevated)',
                    overflow: 'hidden',
                }}>
                    {m.avatar_url
                        ? <img src={m.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initials(m.jmeno, m.prijmeni)}
                </span>
            ))}
            {zbytek > 0 && (
                <span style={{ marginLeft: 5, fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>+{zbytek}</span>
            )}
        </span>
    );
}

/** Dlaždice jako čip (kompaktní panel) — ikona + hodnota, 36 px. */
function Cip({ icon, label, value, valueNode = null, onClick = null, accent = '#3B82F6', live = false, vybrano = false, variant }) {
    const akce = variant === 'action';
    const Wrap = onClick ? 'button' : 'div';
    return (
        <Wrap type={onClick ? 'button' : undefined} onClick={onClick || undefined} aria-pressed={onClick ? vybrano : undefined} title={label}
            style={{
                position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, flexShrink: 0,
                height: 36, padding: '0 12px', borderRadius: 999, font: 'inherit', fontSize: 13.5, fontWeight: 800,
                color: akce ? '#FF8A00' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                cursor: onClick ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent',
                background: 'linear-gradient(150deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                border: akce ? '1.5px dashed rgba(255,138,0,0.65)' : vybrano ? `1.5px solid ${accent}` : '1px solid var(--border-default)',
            }}>
            <span style={{ color: akce ? '#FF8A00' : accent, display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
            {valueNode ?? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value || '—'}</span>}
            {live && <span aria-label="běží" style={{ width: 6, height: 6, borderRadius: 99, background: '#10B981', flexShrink: 0 }} />}
        </Wrap>
    );
}

/** Nabídka ⋯ — věci, které se ťukají jednou za směnu (Ukončit směnu, fáze). */
function Nabidka({ polozky }) {
    const [otevreno, setOtevreno] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!otevreno) return undefined;
        const zavri = (e) => { if (!ref.current?.contains(e.target)) setOtevreno(false); };
        document.addEventListener('pointerdown', zavri);
        return () => document.removeEventListener('pointerdown', zavri);
    }, [otevreno]);
    return (
        <div ref={ref} style={{ position: 'relative', marginLeft: 'auto', flexShrink: 0 }}>
            <button type="button" onClick={() => setOtevreno(o => !o)} aria-label="Další akce" aria-expanded={otevreno} style={{
                width: 36, height: 36, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid var(--border-default)', background: 'linear-gradient(150deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                color: 'var(--text-secondary)', cursor: 'pointer',
            }}><MoreHorizontal size={18} /></button>
            {otevreno && (
                <div role="menu" style={{
                    position: 'absolute', right: 0, top: 44, zIndex: 40, minWidth: 220, padding: 6, borderRadius: 14,
                    background: 'var(--surface-raised)', border: '1px solid var(--border-default)', boxShadow: '0 14px 40px rgba(0,0,0,0.5)',
                }}>
                    {polozky.map(p => (
                        <button key={p.label} type="button" role="menuitem" disabled={p.disabled}
                            onClick={() => { setOtevreno(false); p.onClick?.(); }}
                            style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: 2,
                                minHeight: 46, padding: '8px 12px', borderRadius: 10, border: 'none', background: 'none', textAlign: 'left',
                                font: 'inherit', fontSize: 15, fontWeight: 700, cursor: p.disabled ? 'default' : 'pointer',
                                color: p.disabled ? 'var(--text-muted)' : 'var(--text-primary)',
                            }}>
                            {p.label}
                            {p.popis && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{p.popis}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
